import { Link } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { getActiveBrand, BRAND_PROFILES, setActiveBrand } from "@/lib/brandProfile";

const DEFAULT_LOGO_URL = "/brand/ssp_full.jpg";

const BRAND_CYCLE = ["default", "rrg", "toyota"];

/**
 * Hook that tracks the active brand profile. Re-renders the consumer if the
 * brand is changed mid-session (e.g. via the BrandSwitcher pill).
 */
function useActiveBrand() {
  const [brand, setBrand] = useState(() => getActiveBrand());
  useEffect(() => {
    const onStorage = () => setBrand(getActiveBrand());
    window.addEventListener("storage", onStorage);
    window.addEventListener("ssp-brand-changed", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("ssp-brand-changed", onStorage);
    };
  }, []);
  return brand;
}

/**
 * Long-press hook — for iPad pitch demos where the keyboard shortcut isn't
 * available. Hold the brand logo for ~700ms to cycle the brand. Works with
 * both touch and mouse.
 */
function useLongPress(callback, ms = 700) {
  const timer = useRef(null);
  const fired = useRef(false);
  const start = (e) => {
    fired.current = false;
    timer.current = setTimeout(() => {
      fired.current = true;
      callback(e);
    }, ms);
  };
  const cancel = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };
  return {
    onTouchStart: start,
    onTouchEnd: cancel,
    onTouchCancel: cancel,
    onMouseDown: start,
    onMouseUp: cancel,
    onMouseLeave: cancel,
    // If the long-press fired, also swallow the resulting click so it doesn't
    // navigate the <Link> in the parent.
    onClickCapture: (e) => {
      if (fired.current) {
        e.preventDefault();
        e.stopPropagation();
        fired.current = false;
      }
    },
  };
}

export function BrandMark() {
  const brand = useActiveBrand();

  // Default brand uses the new lime-green artwork logo (emblem + wordmark)
  if (brand.key === "default") {
    return (
      <div className="flex items-center">
        <div className="bg-black rounded-md pl-1.5 pr-2.5 py-1 ring-1 ring-white/10 flex items-center gap-2">
          <img
            src="/brand/ssp_emblem.jpg"
            alt=""
            className="h-9 w-9 object-cover rounded-full"
            data-testid="brand-logo-default-emblem"
          />
          <img
            src="/brand/ssp_wordmark.jpg"
            alt="ServiceSell Pro · Inspect. Advise. Sell. Grow."
            className="h-8 w-auto object-contain"
            data-testid="brand-logo-default"
          />
        </div>
      </div>
    );
  }

  // If a real brand-supplied logo image is configured, render the image
  // AND the text wordmark side-by-side. Historically we only rendered the
  // image, but Steve flagged (Mar 2026) that RRG's tiny round logo tile
  // reads weakly next to Toyota's big wordmark — this gives visual parity.
  if (brand.logoUrl) {
    return (
      <div
        className="flex items-center"
        data-testid={`brand-logo-${brand.key}`}
      >
        <div className="bg-black/40 rounded-md pl-1.5 pr-3 py-1 ring-1 ring-white/10 flex items-center gap-2">
          <img
            src={brand.logoUrl}
            alt={brand.dealerName}
            className="h-9 w-9 object-contain rounded-full"
          />
          <span
            className="font-display font-bold text-2xl tracking-tight leading-none"
            style={{ color: brand.logoTextColor }}
          >
            {brand.logoText || brand.wordmark}
          </span>
          <span className="hidden sm:inline-block text-[10px] uppercase tracking-[0.2em] text-white/55 leading-none border-l border-white/15 pl-2">
            Smart Service
          </span>
        </div>
      </div>
    );
  }

  // Fallback: text wordmark in the brand colour
  return (
    <div
      className="flex items-center"
      data-testid={`brand-logo-${brand.key}`}
    >
      <div className="bg-black/40 rounded-md px-3 py-1.5 ring-1 ring-white/10 flex items-center gap-2">
        <span
          className="font-display font-bold text-2xl tracking-tight leading-none"
          style={{ color: brand.logoTextColor }}
        >
          {brand.logoText || brand.wordmark}
        </span>
        <span className="hidden sm:inline-block text-[10px] uppercase tracking-[0.2em] text-white/55 leading-none border-l border-white/15 pl-2">
          Smart Service
        </span>
      </div>
    </div>
  );
}

export function BrandLink({ to = "/" }) {
  // Long-press the logo (~700ms) to cycle brand — the iPad-friendly trigger.
  const longPress = useLongPress(() => {
    const current = getActiveBrand().key;
    const idx = BRAND_CYCLE.indexOf(current);
    const next = BRAND_CYCLE[(idx + 1) % BRAND_CYCLE.length];
    setActiveBrand(next);
    window.dispatchEvent(new Event("ssp-brand-changed"));
  });

  return (
    <Link
      to={to}
      className="flex items-center group select-none"
      data-testid="brand-link"
      {...longPress}
    >
      <BrandMark />
    </Link>
  );
}

/**
 * Floating "demo mode" pill — only visible when a non-default brand is active.
 * Single click cycles through the brands; long-press / right-click resets to
 * default. Lives at top-right, dismissable by the X.
 */
export function BrandDemoPill() {
  const brand = useActiveBrand();
  const [dismissed, setDismissed] = useState(false);

  if (brand.key === "default" || dismissed) return null;

  const cycle = () => {
    const keys = Object.keys(BRAND_PROFILES);
    const idx = keys.indexOf(brand.key);
    const next = keys[(idx + 1) % keys.length];
    setActiveBrand(next);
    window.dispatchEvent(new Event("ssp-brand-changed"));
  };

  return (
    <div
      className="fixed top-3 right-3 z-[60] flex items-center gap-2 rounded-full bg-black/85 backdrop-blur-sm ring-1 ring-white/20 px-3 py-1.5 shadow-2xl"
      data-testid="brand-demo-pill"
    >
      <span
        className="w-2 h-2 rounded-full animate-pulse"
        style={{ background: brand.logoTextColor }}
      />
      <button
        onClick={cycle}
        className="text-[10px] font-bold uppercase tracking-[0.2em] text-white hover:text-white/80"
        title="Click to cycle brand · Reset via ?brand=default"
        data-testid="brand-demo-cycle"
      >
        {brand.badge}
      </button>
      <button
        onClick={() => {
          setActiveBrand("default");
          window.dispatchEvent(new Event("ssp-brand-changed"));
          setDismissed(true);
        }}
        className="text-white/40 hover:text-white text-xs leading-none px-1"
        title="Reset to default ServiceSell PRO"
        data-testid="brand-demo-reset"
      >
        ×
      </button>
    </div>
  );
}

export const BRAND_LOGO_URL = DEFAULT_LOGO_URL;
